<script setup>
// app/javascript/dashboard/components/CustomDataTable.vue
import { ref, computed } from 'vue';

const props = defineProps({
  headers: {
    type: Array,
    required: true
  },
  rows: {
    type: Array,
    required: true
  },
  selectedColumn: {
    type: String,
    default: ''
  },
  hoverConfig: {
    type: Object,
    default: () => ({
      borderColor: 'border-woot-600',
      backgroundColor: 'bg-woot-100 dark:bg-woot-800',
      textColor: ''
    })
  },
  borderConfig: {
    type: Object,
    default: () => ({
      default: 'border-n-weak',
      hover: 'border-woot-600',
      selected: 'border-woot-600'
    })
  },
  selectionConfig: {
    type: Object,
    default: () => ({
      enabled: true,
      multiSelect: false,
      selectedBg: 'bg-woot-100 dark:bg-woot-800'
    })
  },
  maxHeight: {
    type: String,
    default: 'max-h-56'
  },
  textTruncation: {
    type: Object,
    default: () => ({
      enabled: true,
      maxLength: 50,
      showExpandButton: true
    })
  }
});

const emit = defineEmits(['update:selectedColumn', 'column-click', 'column-hover', 'column-leave', 'cell-click', 'expand-text']);

const hoveredColumn = ref('');

const handleColumnClick = (columnName) => {
  if (!props.selectionConfig.enabled) return;
  
  const nextSelected = props.selectedColumn === columnName ? '' : columnName;
  emit('update:selectedColumn', nextSelected);
  emit('column-click', columnName);
};

const handleColumnHover = (columnName) => {
  hoveredColumn.value = columnName;
  emit('column-hover', columnName);
};

const handleColumnLeave = () => {
  hoveredColumn.value = '';
  emit('column-leave');
};

const handleCellClick = (columnName, rowData, rowIndex) => {
  // Handle column selection for cells too
  if (props.selectionConfig.enabled) {
    handleColumnClick(columnName);
  }
  emit('cell-click', { columnName, rowData, rowIndex });
};

const handleExpandText = (title, content) => {
  emit('expand-text', { title, content });
};

const getTruncatedText = (text, maxLength = 50) => {
  if (!text || !props.textTruncation.enabled) return text || '';
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};

const needsTruncation = (text, maxLength = 50) => {
  return props.textTruncation.enabled && text && text.length > maxLength;
};

const getColumnClasses = (columnName) => {
  const classes = ['px-2', 'py-1', 'border', 'cursor-pointer', 'transition-all', 'duration-200'];
  
  // Always add the default border
  if (props.borderConfig.default) {
    classes.push(props.borderConfig.default);
  }
  
  // Add selection background if column is selected (higher priority)
  if (props.selectedColumn === columnName) {
    if (props.selectionConfig.selectedBg) {
      classes.push(props.selectionConfig.selectedBg);
    }
  }
  // Add hover background effect when column is hovered (only if not selected)
  else if (hoveredColumn.value === columnName && props.hoverConfig.backgroundColor) {
    classes.push(props.hoverConfig.backgroundColor.replace('hover:', ''));
  }
  
  // Add hover border color only if specified and column is hovered
  if (hoveredColumn.value === columnName && props.hoverConfig.borderColor) {
    classes.push(props.hoverConfig.borderColor);
  }
  
  // Add hover text color if specified
  if (hoveredColumn.value === columnName && props.hoverConfig.textColor) {
    classes.push(props.hoverConfig.textColor);
  }
  
  return classes;
};

const getHeaderClasses = (columnName) => {
  const baseClasses = ['bg-n-slate-2', 'dark:bg-n-slate-2', 'text-n-slate-12', 'sticky', 'top-0', 'whitespace-nowrap'];
  return [...baseClasses, ...getColumnClasses(columnName)];
};

const getCellClasses = (columnName) => {
  const baseClasses = ['text-n-slate-11', 'max-h-16', 'overflow-hidden', 'whitespace-nowrap', 'max-w-32', 'text-ellipsis'];
  
  // Add background color logic
  if (props.selectedColumn === columnName) {
    // Selected state - use selection background
    baseClasses.push(props.selectionConfig.selectedBg);
  } else if (hoveredColumn.value === columnName && props.hoverConfig.backgroundColor) {
    // Hovered state - use hover background
    baseClasses.push(props.hoverConfig.backgroundColor.replace('hover:', ''));
  } else {
    // Default state - use default cell background
    baseClasses.push('bg-white', 'dark:bg-n-slate-1');
  }
  
  // Add other column classes except background-related ones
  const otherClasses = getColumnClasses(columnName).filter(cls => 
    !cls.includes('bg-') && !cls.includes('dark:bg-')
  );
  
  return [...baseClasses, ...otherClasses];
};

const getCellDisplayText = (text, maxLength = 20) => {
  if (!text) return '';
  // Convert to string if it's not already a string
  const stringText = text.toString();
  if (stringText.length <= maxLength) return stringText;
  return stringText.substring(0, maxLength) + '...';
};
</script>

<template>
  <div class="overflow-x-auto overflow-y-auto mb-4" :class="maxHeight">
    <div class="border rounded-lg" :class="borderConfig.default">
      <table class="text-sm" style="min-width: max-content">
        <thead>
          <tr>
            <th 
              v-for="header in headers" 
              :key="header" 
              :class="getHeaderClasses(header)"
              @click="handleColumnClick(header)"
              @mouseenter="handleColumnHover(header)"
              @mouseleave="handleColumnLeave"
            >
              {{ header }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(row, rowIndex) in rows" :key="rowIndex">
            <td 
              v-for="header in headers" 
              :key="header" 
              :class="getCellClasses(header)"
              @click="handleCellClick(header, row, rowIndex)"
              @mouseenter="handleColumnHover(header)"
              @mouseleave="handleColumnLeave"
            >
              {{ getCellDisplayText(row[header], 20) }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>